import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme as lightTheme } from '../theme';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'template_login_theme_mode';

const warnModeDisabled = () => {
  if (import.meta?.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn('El modo oscuro está deshabilitado; solo se usa el modo claro corporativo.');
  }
};

const warnMissingProvider = () => {
  if (import.meta?.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn('ThemeModeProvider no está montado; usando modo claro por defecto.');
  }
};

const defaultContextValue: ThemeModeContextValue = {
  mode: 'light',
  toggleTheme: warnModeDisabled,
  setMode: () => warnModeDisabled(),
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mode: ThemeMode = 'light';
  const setMode = (_mode: ThemeMode) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'light');
    }
    warnModeDisabled();
  };

  const toggleTheme = () => {
    warnModeDisabled();
  };

  const value = useMemo(
    () => ({
      mode,
      toggleTheme,
      setMode,
    }),
    []
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    warnMissingProvider();
    return defaultContextValue;
  }
  return context;
};

